import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, Edit, Plus, Image as ImageIcon } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TeamAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', specialization: '', image: '', order: 0 });

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/team`, { withCredentials: true });
      setItems(data.sort((a, b) => a.order - b.order));
    } catch (err) { toast.error('Veri yüklenemedi'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(`${API}/api/upload`, formData, { withCredentials: true });
      setForm({ ...form, image: data.url });
      toast.success('Görsel yüklendi');
    } catch (err) { toast.error('Yükleme başarısız'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API}/api/admin/team/${editing.id}`, form, { withCredentials: true });
        toast.success('Güncellendi');
      } else {
        await axios.post(`${API}/api/admin/team`, form, { withCredentials: true });
        toast.success('Eklendi');
      }
      setEditing(null);
      setForm({ name: '', specialization: '', image: '', order: 0 });
      fetchItems();
    } catch (err) { toast.error('İşlem başarısız'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Emin misiniz?')) return;
    try {
      await axios.delete(`${API}/api/admin/team/${id}`, { withCredentials: true });
      toast.success('Silindi');
      fetchItems();
    } catch (err) { toast.error('Silinemedi'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-slate-900">Hekim Kadromuz</h1>
        <button 
          onClick={() => { setEditing(null); setForm({ name: '', specialization: '', image: '', order: 0 }); }}
          className="bg-primary text-white px-4 py-2 rounded-xl flex items-center gap-2 hover:bg-primary/90 transition-colors text-sm font-medium"
        >
          <Plus className="w-4 h-4" /> Yeni Ekle
        </button>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-1 bg-white p-6 rounded-2xl shadow-sm border border-slate-200 h-fit">
          <h2 className="text-lg font-bold mb-4">{editing ? 'Hekim Düzenle' : 'Yeni Hekim'}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Ad Soyad</label>
              <input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="w-full px-3 py-2 border rounded-xl focus:ring-2 focus:ring-primary outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Uzmanlık / Ünvan</label>
              <input type="text" value={form.specialization} onChange={e => setForm({...form, specialization: e.target.value})} required className="w-full px-3 py-2 border rounded-xl focus:ring-2 focus:ring-primary outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Sıra</label>
              <input type="number" value={form.order} onChange={e => setForm({...form, order: Number(e.target.value)})} className="w-full px-3 py-2 border rounded-xl focus:ring-2 focus:ring-primary outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Görsel</label>
              {form.image && <img src={form.image} alt="Preview" className="h-20 w-auto object-cover rounded-xl mb-2" />}
              <label className="cursor-pointer bg-slate-100 text-slate-600 px-4 py-2 rounded-xl flex items-center justify-center gap-2 hover:bg-slate-200 transition-colors text-sm font-medium">
                <ImageIcon className="w-4 h-4" /> Görsel Yükle
                <input type="file" className="hidden" accept="image/*" onChange={handleUpload} />
              </label>
            </div>
            <div className="flex gap-2 pt-2">
              <button type="submit" className="flex-1 bg-primary text-white py-2 rounded-xl text-sm font-medium hover:bg-primary/90">Kaydet</button>
              {editing && <button type="button" onClick={() => setEditing(null)} className="flex-1 bg-slate-100 text-slate-700 py-2 rounded-xl text-sm font-medium hover:bg-slate-200">İptal</button>}
            </div>
          </form>
        </div>

        <div className="md:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <table className="w-full text-left text-sm text-slate-600">
            <thead className="bg-slate-50 border-b border-slate-100 text-slate-500">
              <tr>
                <th className="px-6 py-3 font-medium">Görsel</th>
                <th className="px-6 py-3 font-medium">Ad Soyad</th>
                <th className="px-6 py-3 font-medium">Uzmanlık</th>
                <th className="px-6 py-3 font-medium text-right">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((item) => (
                <tr key={item.id} className="hover:bg-slate-50/50">
                  <td className="px-6 py-4">
                    <img src={API + item.image} alt="" className="w-12 h-12 rounded-full object-cover bg-slate-100" onError={(e) => { e.target.onerror = null; e.target.src = item.image; }} />
                  </td>
                  <td className="px-6 py-4 font-medium text-slate-900">{item.name}</td>
                  <td className="px-6 py-4">{item.specialization}</td>
                  <td className="px-6 py-4 text-right space-x-2">
                    <button onClick={() => { setEditing(item); setForm(item); }} className="text-blue-600 hover:text-blue-800" title="Düzenle"><Edit className="w-5 h-5 inline" /></button>
                    <button onClick={() => handleDelete(item.id)} className="text-red-500 hover:text-red-700" title="Sil"><Trash2 className="w-5 h-5 inline" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
